import * as amplitude from '@amplitude/analytics-browser';
import i18next from 'i18next';
import { matchPath } from 'react-router-dom';
import { combineEpics } from 'redux-observable';
import { EMPTY } from 'rxjs';
import { filter, ignoreElements, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';

import { addToast } from '../components/various/Toasts';
import { clearToasts } from '../components/various/Toasts/toast';
import {
  fetchConfigurationSuccess,
  fetchOrderSuccess,
  getIsLoggedInAsThisBuyer,
  getIsUserAdmin,
  getOrderDetails,
  getOrderProducts,
  getUserAccess,
  redirectedUserToLatestSelection,
  requestLanguageChange,
  setProductsGridView,
  setProductsListView,
  showModal,
} from '../ducks';
import { paths } from '../paths';
import { isDefined } from '../utils/is';

const RECENT_SELECTION_LOCATION_PATHS = [paths.PRODUCTS_ORDER, paths.HOME, paths.SELECTION_DETAILS, paths.CHECKOUT_ORDER, paths.PAGE_ORDER];

const redirectedToLatestOrderNotificationEpic: AppEpic = (action$, store$) =>
  action$.pipe(
    filter(isActionOf(redirectedUserToLatestSelection)),
    switchMap(() =>
      action$.pipe(
        filter(isActionOf(fetchOrderSuccess)),
        take(1),
        map(() => getOrderProducts(store$.value).length),
        filter(productsCount => productsCount > 0),
        mergeMap(() => {
          const isUserAdmin = getIsUserAdmin(store$.value);
          const userAccess = getUserAccess(store$.value);
          const userType = isUserAdmin ? 'admin' : userAccess;
          const isLoggedInAsThisBuyer = getIsLoggedInAsThisBuyer(store$.value);
          const { buyer, order } = getOrderDetails(store$.value);
          const buyerFullName = `${buyer.firstName} ${buyer.lastName}`.trim();

          const notificationData = { buyerFullName, orderModifiedAt: order.lastModifiedDate };

          const description =
            isLoggedInAsThisBuyer ?
              i18next.t('selections:recent_selection_hint')
            : i18next.t('selections:recent_selection_hint_another_buyer', notificationData);

          const maybeBuyerFullName = !isLoggedInAsThisBuyer ? ` (${buyerFullName})` : '';

          const getShouldCloseOnLocation = (locationPathname: string) => {
            const currentPath = RECENT_SELECTION_LOCATION_PATHS.find(path => matchPath(path, locationPathname));

            if (!isDefined(currentPath)) {
              return true;
            }

            const matchingLocation = matchPath(currentPath, locationPathname);
            const locationOrderId = matchingLocation?.params.id;
            const isOtherLocation = !matchingLocation?.pattern.end;
            const isOtherOrderId = isDefined(order.order) && isDefined(locationOrderId) && locationOrderId !== order.order;

            return isOtherLocation || isOtherOrderId;
          };

          amplitude.identify(new amplitude.Identify().set('userType', userType));
          amplitude.track('notification.recentselection.view');

          addToast(
            <span data-sentry-mask>
              {i18next.t('selections:recent_selection_loaded')}
              {maybeBuyerFullName}
            </span>,
            {
              actions: [
                {
                  action: showModal('CreateOrderModal'),
                  label: i18next.t('selections:create_new_selection'),
                  onClick: () => {
                    amplitude.track('notification.button.createnew.click');
                  },
                },
                {
                  label: i18next.t('selections:see_all_selections'),
                  onClick: () => {
                    amplitude.track('notification.button.seeall.click');
                  },
                  to: paths.SELECTIONS,
                },
              ],
              dataTestId: 'recentSelectionLoadedToast',
              description: <span data-sentry-mask>{description}</span>,
              duration: 0,
              getShouldCloseOnLocation,
              onClose: () => {
                amplitude.track('notification.link.close.click');
              },
            },
          );

          return EMPTY;
        }),
      ),
    ),
  );

const requestLanguageChangeEpic: AppEpic = action$ =>
  action$.pipe(
    filter(isActionOf(requestLanguageChange)),
    tap(() => clearToasts()),
    ignoreElements(),
  );

const setAllProductsViewLayoutEpic: AppEpic = action$ =>
  action$.pipe(
    filter(isActionOf(fetchConfigurationSuccess)),
    map(({ payload }) => (payload.allProductsViewLayout === 'list' ? setProductsListView() : setProductsGridView())),
  );

export const uiEpic = combineEpics(redirectedToLatestOrderNotificationEpic, setAllProductsViewLayoutEpic, requestLanguageChangeEpic);
